import Delaunator from "delaunator";
import seedrandom from "seedrandom";
import type { Neighborhood, PlacementSocket, Plot, Vector3Value, VillagePath, WorldState } from "./types.js";

type Point2 = [number, number];

const PLOT_TEMPLATES = ["grove", "bend", "creekside", "wedge", "hill", "meadow"] as const;

/**
 * Bumped whenever plot positions change, so guilds holding a world generated by an older
 * layout regenerate instead of loading one the renderer assumes is correctly spaced.
 * Plot ids are stable across versions, so ownership survives the regeneration.
 *
 * Version 3 replaced the ring layout. Plots used to sit on concentric rings at radius 18, 36
 * and 54 around seven neighborhood centres, which read from above as exactly what it was —
 * a set of circles. A hamlet is not laid out on rings: houses sit where the ground allowed,
 * lanes wander between them, and the leftover ground becomes fields. This version samples
 * positions organically and derives both the lanes and the field boundaries from the spaces
 * between the houses rather than imposing a shape on them.
 */
export const WORLD_GENERATOR_VERSION = 5;
export const WORLD_CHUNK_ID = `world-v${WORLD_GENERATOR_VERSION}`;

/**
 * All distances below are in **voxels**, matching the renderer's grid.
 *
 * They used to be in an abstract world unit roughly half this size, which was fine while the
 * renderer built its own cottages to suit. The voxel cottages are 14 to 20 voxels wide, so a
 * plot of the old size could not physically contain one. Rather than scatter a conversion
 * factor through the generator, the generator now speaks the renderer's units and the renderer
 * multiplies by its voxel size exactly once, when it builds geometry.
 */

/**
 * How far apart plot centres sit.
 *
 * Sized for the *largest* a parcel can grow to, not the size it starts at — a member who expands
 * to the last tier must not end up overlapping the neighbour who did the same. The world grows to
 * fit rather than the plot count shrinking: `hamletRadius` derives the hamlet's size from how many
 * plots are wanted, so widening this spreads the village out instead of housing fewer people.
 */
export const MIN_PLOT_DISTANCE = 44;
/**
 * The authored village at the centre of the world — river, bridge, mills, well and the roads
 * between them. It is the same for every guild and no member plot is ever placed inside it,
 * so the heart of the village is art-directed and only the edge is generated.
 */
export const CORE_RADIUS = 58;
/** Retained name for the keep-out radius, which is now the authored core. */
export const TOWN_SQUARE_CLEARANCE = CORE_RADIUS;

const TAU = Math.PI * 2;

/**
 * The hamlet's outline, as a radius that varies with bearing.
 *
 * A circular boundary produces a circular village however organically the interior is
 * sampled, because the outermost ring of houses traces it. Two low harmonics are enough to
 * make the edge wander without it reading as a deliberate shape.
 */
function boundaryRadius(angle: number, base: number, phase: number): number {
  return base * (1 + 0.2 * Math.sin(angle * 3 + phase) + 0.12 * Math.cos(angle * 5 - phase * 1.7));
}

/** Radius that fits `count` plots at the minimum spacing, with packing slack. */
function hamletRadius(count: number): number {
  // Area per plot at a realistic dart-thrown packing density, plus the plaza it wraps around.
  const perPlot = MIN_PLOT_DISTANCE * MIN_PLOT_DISTANCE * 0.95;
  const plaza = Math.PI * TOWN_SQUARE_CLEARANCE * TOWN_SQUARE_CLEARANCE;
  return Math.sqrt((count * perPlot + plaza) / Math.PI);
}

/** Half-extent of the inhabited world, for the renderer's terrain, fog and camera ranges. */
export function worldExtent(neighborhoodCount = 7, plotsPerNeighborhood = 15): number {
  const base = hamletRadius(neighborhoodCount * plotsPerNeighborhood);
  // The widest the wandering boundary can reach, plus the woodland that frames it.
  return base * 1.32 + 92;
}

function distance(a: Point2, b: Point2): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/**
 * The rolling ground the village sits on. The renderer displaces its terrain mesh with this
 * exact function so buildings land flush instead of floating above a flat plane.
 */
export function terrainHeight(x: number, z: number, seedOffset: number): number {
  return Math.sin((x + seedOffset) * 0.055) * 1.45
    + Math.cos((z - seedOffset) * 0.047) * 1.15
    + Math.sin(x * 0.115 + z * 0.098 + seedOffset) * 0.5
    + Math.cos(x * 0.021 - z * 0.026) * 0.75;
}

/**
 * Recovers the terrain offset for a seed. It is the first draw of the seeded generator, so the
 * renderer can rebuild it from the world seed alone without widening the persisted schema.
 */
export function terrainOffsetForSeed(seed: string): number {
  return Math.floor(seedrandom(seed)() * 10_000);
}

/**
 * A coarse bucket grid, so placing the hundredth plot does not test the ninety-nine before it.
 */
class SpatialGrid {
  private readonly cells = new Map<number, Point2[]>();
  private readonly cell = MIN_PLOT_DISTANCE;

  private key(x: number, z: number): number {
    return (Math.floor(x / this.cell) + 512) * 1024 + (Math.floor(z / this.cell) + 512);
  }

  add(point: Point2): void {
    const key = this.key(point[0], point[1]);
    const bucket = this.cells.get(key);
    if (bucket) bucket.push(point);
    else this.cells.set(key, [point]);
  }

  /** True when nothing already placed lies within `MIN_PLOT_DISTANCE` of the candidate. */
  clear(candidate: Point2): boolean {
    const cx = Math.floor(candidate[0] / this.cell);
    const cz = Math.floor(candidate[1] / this.cell);
    for (let x = cx - 1; x <= cx + 1; x += 1) {
      for (let z = cz - 1; z <= cz + 1; z += 1) {
        const bucket = this.cells.get((x + 512) * 1024 + (z + 512));
        if (!bucket) continue;
        for (const point of bucket) {
          if (distance(point, candidate) < MIN_PLOT_DISTANCE) return false;
        }
      }
    }
    return true;
  }
}

/**
 * Scatters plot positions through the hamlet by dart throwing.
 *
 * Poisson-style rejection rather than a ring allocation: every position is as far from its
 * neighbours as the spacing demands and no further, so the result is even without being
 * regular. The radial bias concentrates houses near the square and thins them toward the
 * edge, which is what stops the hamlet reading as a uniform field of houses.
 *
 * The boundary grows if darts stop landing, so the loop always terminates however unlucky the
 * seed is.
 */
function samplePlots(rng: seedrandom.PRNG, count: number): Point2[] {
  const phase = rng() * TAU;
  let base = hamletRadius(count);
  const placed: Point2[] = [];
  const grid = new SpatialGrid();

  for (let index = 0; index < count; index += 1) {
    let attempts = 0;
    let point: Point2 | null = null;
    while (!point) {
      attempts += 1;
      if (attempts > 500) {
        base *= 1.05;
        attempts = 0;
      }
      const angle = rng() * TAU;
      // An exponent below one pulls samples inward, so the core is denser than the fringe.
      const spread = Math.pow(rng(), 0.62);
      const edge = boundaryRadius(angle, base, phase);
      const radius = TOWN_SQUARE_CLEARANCE + spread * Math.max(1, edge - TOWN_SQUARE_CLEARANCE);
      const candidate: Point2 = [Math.cos(angle) * radius, Math.sin(angle) * radius];
      if (Math.hypot(candidate[0], candidate[1]) < TOWN_SQUARE_CLEARANCE) continue;
      if (!grid.clear(candidate)) continue;
      point = candidate;
    }
    grid.add(point);
    placed.push(point);
  }
  return placed;
}

/**
 * Groups the scattered plots into neighborhoods by k-means.
 *
 * Neighborhoods are a real feature — they name where someone lives and drive plot assignment
 * — so they still have to exist. What changes is that they are now discovered from where the
 * houses ended up rather than being the shape the houses were poured into.
 */
function clusterPlots(points: Point2[], groups: number, rng: seedrandom.PRNG): number[] {
  const centroids: Point2[] = [];
  // Farthest-point initialisation: pick a random first seed, then repeatedly take the point
  // furthest from every seed so far. Deterministic, and it never starts with two seeds on
  // top of each other the way random initialisation can.
  centroids.push(points[Math.floor(rng() * points.length)] ?? [0, 0]);
  while (centroids.length < groups) {
    let best: Point2 = points[0]!;
    let bestGap = -1;
    for (const point of points) {
      let nearest = Infinity;
      for (const centroid of centroids) nearest = Math.min(nearest, distance(point, centroid));
      if (nearest > bestGap) {
        bestGap = nearest;
        best = point;
      }
    }
    centroids.push(best);
  }

  const assignment = new Array<number>(points.length).fill(0);
  for (let pass = 0; pass < 16; pass += 1) {
    let moved = false;
    for (let index = 0; index < points.length; index += 1) {
      let nearest = 0;
      let nearestGap = Infinity;
      for (let group = 0; group < centroids.length; group += 1) {
        const gap = distance(points[index]!, centroids[group]!);
        if (gap < nearestGap) {
          nearestGap = gap;
          nearest = group;
        }
      }
      if (assignment[index] !== nearest) {
        assignment[index] = nearest;
        moved = true;
      }
    }
    const sums = centroids.map<Point2>(() => [0, 0]);
    const counts = new Array<number>(centroids.length).fill(0);
    for (let index = 0; index < points.length; index += 1) {
      const group = assignment[index]!;
      sums[group]![0] += points[index]![0];
      sums[group]![1] += points[index]![1];
      counts[group]! += 1;
    }
    for (let group = 0; group < centroids.length; group += 1) {
      if (counts[group]! === 0) continue;
      centroids[group] = [sums[group]![0] / counts[group]!, sums[group]![1] / counts[group]!];
    }
    if (!moved) break;
  }

  // An empty cluster would leave a neighborhood with no plots, which `assignNextPlot` and the
  // mini-map both assume cannot happen. Donate from the largest.
  for (let group = 0; group < groups; group += 1) {
    if (assignment.includes(group)) continue;
    const tally = new Array<number>(groups).fill(0);
    for (const value of assignment) tally[value]! += 1;
    const largest = tally.indexOf(Math.max(...tally));
    const donor = assignment.indexOf(largest);
    if (donor >= 0) assignment[donor] = group;
  }
  return assignment;
}

/** A parcel boundary, and the two plots it separates. `-1` means the far side is open ground. */
type Segment = { a: Point2; b: Point2; left: number; right: number };

function circumcenter(ax: number, az: number, bx: number, bz: number, cx: number, cz: number): Point2 | null {
  const dx = bx - ax;
  const dz = bz - az;
  const ex = cx - ax;
  const ez = cz - az;
  const bl = dx * dx + dz * dz;
  const cl = ex * ex + ez * ez;
  const d = 2 * (dx * ez - dz * ex);
  if (Math.abs(d) < 1e-9) return null;
  return [ax + (ez * bl - dz * cl) / d, az + (dx * cl - ex * bl) / d];
}

/**
 * The boundaries between neighbouring plots, as the dual of the plot triangulation.
 *
 * Every one of these lines is equidistant from the two houses either side of it, which is
 * exactly where people put a hedge. Taken together they carve the hamlet into irregular
 * parcels of the kind the reference is full of — and because they run *between* houses rather
 * than through them, a subset of the same lines makes the lane network.
 *
 * Ghost points ringing the hamlet keep the outermost cells bounded; without them the edge
 * cells are open and their circumcentres fly off to infinity.
 */
function parcelEdges(points: Point2[], base: number, phase: number): Segment[] {
  const ghosts: Point2[] = [];
  for (let index = 0; index < 48; index += 1) {
    const angle = (index / 48) * TAU;
    const radius = boundaryRadius(angle, base, phase) + 26;
    ghosts.push([Math.cos(angle) * radius, Math.sin(angle) * radius]);
  }
  const all = [...points, ...ghosts];
  const triangulation = Delaunator.from(all);
  const { triangles, halfedges } = triangulation;

  const centers: Array<Point2 | null> = [];
  for (let t = 0; t < triangles.length / 3; t += 1) {
    const a = triangles[t * 3]!;
    const b = triangles[t * 3 + 1]!;
    const c = triangles[t * 3 + 2]!;
    centers.push(
      circumcenter(all[a]![0], all[a]![1], all[b]![0], all[b]![1], all[c]![0], all[c]![1])
    );
  }

  const segments: Segment[] = [];
  const realCount = points.length;
  for (let e = 0; e < halfedges.length; e += 1) {
    const opposite = halfedges[e]!;
    // Each interior edge is shared by two halfedges; take it once.
    if (opposite < e) continue;
    const from = centers[Math.floor(e / 3)];
    const to = centers[Math.floor(opposite / 3)];
    if (!from || !to) continue;
    const length = distance(from, to);
    // Sliver triangles throw their circumcentres a long way out. A boundary between two
    // houses is never longer than the spacing between them by much.
    if (length < 1.5 || length > MIN_PLOT_DISTANCE * 2.2) continue;
    const midX = (from[0] + to[0]) / 2;
    const midZ = (from[1] + to[1]) / 2;
    const fromCenter = Math.hypot(midX, midZ);
    if (fromCenter < TOWN_SQUARE_CLEARANCE * 0.7) continue;
    if (fromCenter > boundaryRadius(Math.atan2(midZ, midX), base, phase) + 22) continue;
    // The dual of this Voronoi edge is the Delaunay edge between these two sites, which are
    // exactly the plots whose cells it divides.
    const next = e % 3 === 2 ? e - 2 : e + 1;
    const left = triangles[e]!;
    const right = triangles[next]!;
    segments.push({
      a: from,
      b: to,
      left: left < realCount ? left : -1,
      right: right < realCount ? right : -1
    });
  }
  return segments;
}

/**
 * Promotes a subset of the parcel boundaries to lanes, in three passes.
 *
 * The first pass is the one that matters: every plot must have at least one of its own
 * boundaries promoted, because a house with only hedges around it has no way out and reads as
 * dropped into the middle of a field. Because each boundary serves the two plots either side
 * of it, covering every house takes only about half the edges — which leaves the rest to be
 * hedges, and that ratio is what produces lanes threading through hedged parcels rather than
 * a paved grid.
 *
 * The second pass joins whatever the first left as separate fragments, via a minimum spanning
 * tree so the joins are short. The third adds a share of the leftovers back to close the
 * network into loops, because a pure tree reads as a branching river and the reference's
 * lanes plainly come back round on themselves.
 */
function chooseLanes(segments: Segment[], plotCount: number, rng: seedrandom.PRNG): boolean[] {
  const nodeIds = new Map<string, number>();
  const key = (point: Point2) => `${Math.round(point[0] * 4)},${Math.round(point[1] * 4)}`;
  const idFor = (point: Point2): number => {
    const k = key(point);
    const existing = nodeIds.get(k);
    if (existing !== undefined) return existing;
    const id = nodeIds.size;
    nodeIds.set(k, id);
    return id;
  };

  const edges = segments.map((segment, index) => ({
    index,
    a: idFor(segment.a),
    b: idFor(segment.b),
    length: distance(segment.a, segment.b)
  }));
  const order = [...edges].sort((left, right) => left.length - right.length);

  const parent = new Array<number>(nodeIds.size).fill(0).map((_, index) => index);
  const find = (value: number): number => {
    while (parent[value] !== value) {
      parent[value] = parent[parent[value]!]!;
      value = parent[value]!;
    }
    return value;
  };

  const isLane = new Array<boolean>(segments.length).fill(false);
  const union = (a: number, b: number): boolean => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA === rootB) return false;
    parent[rootA] = rootB;
    return true;
  };
  const promote = (index: number): void => {
    if (isLane[index]) return;
    isLane[index] = true;
    union(edges[index]!.a, edges[index]!.b);
  };

  // Pass one: every house gets a lane along one of its own boundaries, the shortest available.
  const served = new Array<boolean>(plotCount).fill(false);
  for (const edge of order) {
    const segment = segments[edge.index]!;
    const needsLeft = segment.left >= 0 && !served[segment.left];
    const needsRight = segment.right >= 0 && !served[segment.right];
    if (!needsLeft && !needsRight) continue;
    promote(edge.index);
    if (segment.left >= 0) served[segment.left] = true;
    if (segment.right >= 0) served[segment.right] = true;
  }

  // Pass two: join the fragments that leaves behind.
  const leftovers: number[] = [];
  for (const edge of order) {
    if (isLane[edge.index]) continue;
    if (union(edge.a, edge.b)) isLane[edge.index] = true;
    else leftovers.push(edge.index);
  }

  // Pass three: close a share of it back into loops.
  const extra = Math.floor(leftovers.length * 0.22);
  for (let index = 0; index < extra; index += 1) {
    isLane[leftovers[Math.floor(rng() * leftovers.length)]!] = true;
  }
  return isLane;
}

/** Turns a straight segment into a gently bowed polyline sitting on the terrain. */
function curveBetween(a: Point2, b: Point2, bend: number, seedOffset: number, steps = 5): Vector3Value[] {
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  const length = Math.max(1, Math.hypot(dx, dz));
  const normal: Point2 = [-dz / length, dx / length];
  return Array.from({ length: steps }, (_, index) => {
    const t = index / (steps - 1);
    const wave = Math.sin(t * Math.PI) * bend;
    const x = a[0] + dx * t + normal[0] * wave;
    const z = a[1] + dz * t + normal[1] * wave;
    return { x, y: terrainHeight(x, z, seedOffset), z };
  });
}

/**
 * Placement sockets, in plot-local voxels.
 *
 * These are on their way out: free-form placement replaces them, and a placement will carry
 * its own position, rotation and scale instead of pointing at a fixed slot. They are kept for
 * now, scaled with the rest of the world, so the existing yard UI keeps working while the
 * renderer is replaced underneath it.
 */
function makeSockets(plotId: string): PlacementSocket[] {
  const sockets: PlacementSocket[] = [
    { id: `${plotId}-house`, type: "house", position: { x: 0, y: 0, z: 0 }, rotationY: 0, allowedCategories: ["house"], allowedRotations: [0] },
    { id: `${plotId}-garden`, type: "garden", position: { x: 9.6, y: 0, z: 2.8 }, rotationY: -0.16, allowedCategories: ["exterior"], allowedRotations: [0, Math.PI] },
    { id: `${plotId}-porch`, type: "porch", position: { x: 0.8, y: 0, z: 6.4 }, rotationY: 0, allowedCategories: ["exterior", "rare"], allowedRotations: [0, Math.PI / 2, Math.PI, -Math.PI / 2] }
  ];
  for (let index = 0; index < 10; index += 1) {
    const angle = (index / 10) * Math.PI * 2 + 0.3;
    sockets.push({
      id: `${plotId}-decor-${index}`,
      type: index % 3 === 0 ? "exterior-medium" : "exterior-small",
      position: { x: Math.cos(angle) * (8.4 + (index % 2) * 2), y: 0, z: Math.sin(angle) * (7.2 + ((index + 1) % 2) * 2) },
      rotationY: angle + Math.PI,
      allowedCategories: ["exterior", "rare"],
      allowedRotations: [0, Math.PI / 2, Math.PI, -Math.PI / 2]
    });
  }
  for (let index = 0; index < 4; index += 1) {
    sockets.push({
      id: `${plotId}-plant-${index}`,
      type: "plant-bed",
      position: { x: 8.2 + (index % 2) * 2.4, y: 0, z: -1.6 + Math.floor(index / 2) * 2.4 },
      rotationY: 0,
      allowedCategories: ["plant"],
      allowedRotations: [0]
    });
  }
  for (let index = 0; index < 3; index += 1) {
    sockets.push({
      id: `${plotId}-pet-${index}`,
      type: "pet",
      position: { x: -4 + index * 2.8, y: 0, z: 8.4 },
      rotationY: 0,
      allowedCategories: ["pet"],
      allowedRotations: [0]
    });
  }
  return sockets;
}

/** The closest point on any of the given segments, and how far away it is. */
function nearestOnSegments(point: Point2, segments: Segment[]): { at: Point2; gap: number } {
  let at: Point2 = point;
  let gap = Infinity;
  for (const segment of segments) {
    const dx = segment.b[0] - segment.a[0];
    const dz = segment.b[1] - segment.a[1];
    const lengthSquared = dx * dx + dz * dz;
    const t = lengthSquared > 0
      ? Math.max(0, Math.min(1, ((point[0] - segment.a[0]) * dx + (point[1] - segment.a[1]) * dz) / lengthSquared))
      : 0;
    const px = segment.a[0] + dx * t;
    const pz = segment.a[1] + dz * t;
    const candidate = Math.hypot(point[0] - px, point[1] - pz);
    if (candidate < gap) {
      gap = candidate;
      at = [px, pz];
    }
  }
  return { at, gap };
}

export function generateWorld(seed: string, neighborhoodCount = 7, plotsPerNeighborhood = 15): WorldState {
  const rng = seedrandom(seed);
  // Must stay the first draw: `terrainOffsetForSeed` reproduces it from the seed alone.
  const seedOffset = Math.floor(rng() * 10_000);
  const total = neighborhoodCount * plotsPerNeighborhood;
  const base = hamletRadius(total);

  const points = samplePlots(rng, total);
  const phase = rng() * TAU;
  const segments = parcelEdges(points, base, phase);
  const isLane = chooseLanes(segments, points.length, rng);

  const laneSegments = segments.filter((_, index) => isLane[index]);

  /**
   * Front paths for any house the boundary network left stranded.
   *
   * A plot on the fringe can have every one of its own boundaries filtered out — too long,
   * or reaching past the hamlet edge — and so never get a lane in the covering pass. Rather
   * than loosen the filters and let stray boundaries wander off into the fields, give those
   * houses the short front path a real cottage would have.
   *
   * These are tagged apart from lanes because they meet the network mid-span rather than at a
   * junction, and because a front path should be narrower than the lane it joins.
   */
  const spurSegments: Segment[] = [];
  for (const point of points) {
    const nearest = nearestOnSegments(point, laneSegments);
    if (nearest.gap <= MIN_PLOT_DISTANCE * 0.55) continue;
    spurSegments.push({ a: point, b: nearest.at, left: -1, right: -1 });
  }

  const lanes = [...laneSegments, ...spurSegments];
  const paths: VillagePath[] = [
    ...laneSegments.map((lane, index) => ({
      id: `path-lane-${index + 1}`,
      points: curveBetween(lane.a, lane.b, (rng() - 0.5) * Math.min(3, distance(lane.a, lane.b) * 0.16), seedOffset)
    })),
    ...spurSegments.map((spur, index) => ({
      id: `path-spur-${index + 1}`,
      points: curveBetween(spur.a, spur.b, (rng() - 0.5) * 0.6, seedOffset, 3)
    }))
  ];
  const hedges: VillagePath[] = segments
    .filter((_, index) => !isLane[index])
    .map((edge, index) => ({
      id: `hedge-${index + 1}`,
      points: curveBetween(edge.a, edge.b, (rng() - 0.5) * 1.2, seedOffset, 3)
    }));

  const assignment = clusterPlots(points, neighborhoodCount, rng);
  const counters = new Array<number>(neighborhoodCount).fill(0);
  const plots: Plot[] = points.map((point, index) => {
    const group = assignment[index]!;
    counters[group]! += 1;
    const id = `plot-${group + 1}-${String(counters[group]!).padStart(2, "0")}`;
    const [x, z] = point;
    // Face the lane it fronts onto, or the square if it somehow has no lane near it.
    const nearest = nearestOnSegments(point, lanes);
    const facing = Number.isFinite(nearest.gap) && nearest.gap > 0.01
      ? Math.atan2(nearest.at[0] - x, nearest.at[1] - z)
      : Math.atan2(-x, -z);
    return {
      id,
      neighborhoodId: `neighborhood-${group + 1}`,
      template: PLOT_TEMPLATES[(index + group) % PLOT_TEMPLATES.length]!,
      position: { x, y: terrainHeight(x, z, seedOffset), z },
      rotationY: facing + (rng() - 0.5) * 0.18,
      elevation: terrainHeight(x, z, seedOffset),
      assignedOwnerId: null,
      sockets: makeSockets(id)
    };
  });

  const neighborhoods: Neighborhood[] = Array.from({ length: neighborhoodCount }, (_, group) => {
    const own = plots.filter((plot) => plot.neighborhoodId === `neighborhood-${group + 1}`);
    const centerX = own.reduce((total, plot) => total + plot.position.x, 0) / Math.max(1, own.length);
    const centerZ = own.reduce((total, plot) => total + plot.position.z, 0) / Math.max(1, own.length);
    return {
      id: `neighborhood-${group + 1}`,
      center: { x: centerX, y: terrainHeight(centerX, centerZ, seedOffset), z: centerZ },
      plotIds: own.map((plot) => plot.id)
    };
  });

  return { seed, neighborhoods, plots, paths, hedges, generation: 0, generatedAt: new Date(0).toISOString() };
}

/** A satellite hamlet is three neighborhoods, which is roughly half a year of new members. */
export const EXPANSION_NEIGHBORHOODS = 3;
export const EXPANSION_PLOTS_PER_NEIGHBORHOOD = 15;
/** Expand once the frontier gets this close to full, rather than at the moment it runs out. */
export const EXPANSION_THRESHOLD = 12;

/** How far out the village currently reaches, measured rather than assumed. */
function occupiedRadius(world: WorldState): number {
  return world.plots.reduce((furthest, plot) => Math.max(furthest, Math.hypot(plot.position.x, plot.position.z)), 0);
}

/**
 * Another hamlet, out past everything already standing.
 *
 * The generator samples the whole village at once — points, lanes and clusters all derive from
 * the full set — so asking it for more plots does not extend a village, it produces a different
 * one. Every parcel would move, and a member's home with it. So growth is additive instead: a
 * fresh hamlet from a derived seed, translated out beyond the current edge and merged in.
 *
 * Nothing already accepted is touched, which is what keeps parcel transforms byte-for-byte stable
 * across a growth event. The ring is laid out with its own seed but stands on the *core's*
 * terrain, because the renderer recovers the ground from `world.seed` alone and a satellite on
 * its own offset would sit on a different landscape to the one around it.
 */
export function expandWorld(world: WorldState): WorldState {
  const generation = (world.generation ?? 0) + 1;
  const ring = generateWorld(`${world.seed}-ring-${generation}`, EXPANSION_NEIGHBORHOODS, EXPANSION_PLOTS_PER_NEIGHBORHOOD);
  const seedOffset = terrainOffsetForSeed(world.seed);

  // Golden angle, so successive hamlets spread around the village instead of marching off in a
  // line, and the bearing for a given generation is always the same.
  const bearing = generation * 2.399963;
  /**
   * Far enough out that no new cottage can crowd an old one.
   *
   * Both radii are measured rather than estimated, so the triangle inequality does the rest: a
   * core plot sits within `occupiedRadius(world)` of the origin and a ring plot within
   * `occupiedRadius(ring)` of the ring's centre, which leaves at least `MIN_PLOT_DISTANCE`
   * between any pair of them. Estimating this from `worldExtent` put the two hamlets 32 units
   * apart, and a cottage is wider than that.
   */
  const reach = occupiedRadius(world) + occupiedRadius(ring) + MIN_PLOT_DISTANCE;
  const dx = Math.cos(bearing) * reach;
  const dz = Math.sin(bearing) * reach;

  const plotId = (id: string) => `${id}-g${generation}`;
  const neighborhoodId = (id: string) => `${id}-g${generation}`;
  const movePath = (path: VillagePath, prefix: string): VillagePath => ({
    id: `${prefix}-${path.id}`,
    points: path.points.map((point) => ({ x: point.x + dx, y: terrainHeight(point.x + dx, point.z + dz, seedOffset), z: point.z + dz }))
  });

  const plots: Plot[] = ring.plots.map((plot) => {
    const x = plot.position.x + dx;
    const z = plot.position.z + dz;
    const height = terrainHeight(x, z, seedOffset);
    return {
      ...plot,
      id: plotId(plot.id),
      neighborhoodId: neighborhoodId(plot.neighborhoodId),
      position: { x, y: height, z },
      elevation: height,
      assignedOwnerId: null,
      sockets: makeSockets(plotId(plot.id))
    };
  });

  const neighborhoods: Neighborhood[] = ring.neighborhoods.map((neighborhood) => {
    const x = neighborhood.center.x + dx;
    const z = neighborhood.center.z + dz;
    return {
      id: neighborhoodId(neighborhood.id),
      center: { x, y: terrainHeight(x, z, seedOffset), z },
      plotIds: neighborhood.plotIds.map(plotId)
    };
  });

  return {
    ...world,
    generation,
    neighborhoods: [...world.neighborhoods, ...neighborhoods],
    plots: [...world.plots, ...plots],
    paths: [...world.paths, ...ring.paths.map((path) => movePath(path, `g${generation}`))],
    hedges: [...(world.hedges ?? []), ...(ring.hedges ?? []).map((hedge) => movePath(hedge, `g${generation}`))]
  };
}

/** Whether the village is close enough to full that it should grow before someone is turned away. */
export function needsExpansion(world: WorldState): boolean {
  return world.plots.filter((plot) => !plot.assignedOwnerId).length <= EXPANSION_THRESHOLD;
}

export function assignNextPlot(world: WorldState, ownerId: string): WorldState {
  if (world.plots.some((plot) => plot.assignedOwnerId === ownerId)) return world;
  const available = world.plots.filter((plot) => !plot.assignedOwnerId);
  if (available.length === 0) throw new Error("The current village has no available plots");
  const assignedCount = world.plots.length - available.length;
  const frontierNeighborhood = Math.min(world.neighborhoods.length - 1, Math.floor(assignedCount / 12));
  const preferred = available.filter((plot) => plot.neighborhoodId === `neighborhood-${frontierNeighborhood + 1}`);
  const selected = (preferred.length ? preferred : available).sort((a, b) =>
    Math.hypot(a.position.x, a.position.z) - Math.hypot(b.position.x, b.position.z)
  )[0]!;
  return {
    ...world,
    plots: world.plots.map((plot) => plot.id === selected.id ? { ...plot, assignedOwnerId: ownerId } : plot)
  };
}
